import {
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import type { TranslationKey } from "../../shared/i18n/translations";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { AppModal } from "../../shared/components/AppModal";
import {
  changeCrmStatus,
  convertCrmLead,
  createCrmQuotation,
  getCrm,
  saveCrm,
  type CrmRecord,
  type CrmSnapshot,
} from "./crmApi";

export type CrmSection =
  | "overview"
  | "leads"
  | "contacts"
  | "opportunities"
  | "activities"
  | "campaigns"
  | "campaignMembers"
  | "consents"
  | "pipeline"
  | "reports";
const k = (value: string) => value as TranslationKey;
const collections: Record<CrmSection, keyof CrmSnapshot | ""> = {
  overview: "",
  leads: "leads",
  contacts: "contacts",
  opportunities: "opportunities",
  activities: "activities",
  campaigns: "campaigns",
  campaignMembers: "campaignMembers",
  consents: "consents",
  pipeline: "stages",
  reports: "",
};
const entities: Record<CrmSection, string> = {
  overview: "",
  leads: "leads",
  contacts: "contacts",
  opportunities: "opportunities",
  activities: "activities",
  campaigns: "campaigns",
  campaignMembers: "campaign-members",
  consents: "consents",
  pipeline: "stages",
  reports: "",
};

export function CrmPage({ section = "overview" }: { section?: CrmSection }) {
  const { user } = useAuth(),
    { t, formatNumber } = useLocale(),
    [data, setData] = useState<CrmSnapshot>(),
    [error, setError] = useState<string>(),
    [editing, setEditing] = useState<CrmRecord | null | undefined>();
  const load = async () => {
    try {
      setData(await getCrm());
      setError(undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "crm.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const values = collections[section] && data ? data[collections[section]] : [];
  const list = useClientRecordList(
    (Array.isArray(values) ? values : []) as CrmRecord[],
    `crm:${section}`,
  );
  if (error)
    return (
      <main className="page-content">
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      </main>
    );
  if (!data)
    return (
      <main className="page-content">
        <p>{t(k("crm.loading"))}</p>
      </main>
    );
  if (editing !== undefined) {
    const recordIndex = editing ? list.rows.findIndex((record) => record.id === editing.id) : -1;
    return (
      <CrmEditor
        section={section}
        item={editing}
        data={data}
        cancel={() => setEditing(undefined)}
        saved={async () => {
          setEditing(undefined);
          await load();
        }}
        onPrevious={recordIndex > 0 ? () => setEditing(list.rows[recordIndex - 1]) : null}
        onNext={recordIndex >= 0 && recordIndex < list.rows.length - 1 ? () => setEditing(list.rows[recordIndex + 1]) : null}
      />
    );
  }
  const writable = user?.permissions.crm === "Write";
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t(k("crm.eyebrow"))}</span>
          <h1>{t(k(`crm.${section}`))}</h1>
          <p>{t(k(`crm.${section}Description`))}</p>
        </div>
      </header>
      {section === "overview" ? (
        <Overview data={data} />
      ) : section === "reports" ? (
        <Reports data={data} />
      ) : (
        <section className="data-panel administration-panel">
          <div className="section-heading">
            <h2>{t(k(`crm.${section}`))}</h2>
            {writable && (
              <button
                className="primary-button"
                onClick={() => setEditing(null)}
              >
                + {t("administration.add")}
              </button>
            )}
          </div>
          <ClientRecordListControls
            list={list}
            formatStatus={(status) => t(k(`crm.status.${status}`))}
          />
          <div className="administration-table-wrap">
            <table className="data-table administration-table">
              <thead>
                <tr>
                  <SortableHeader label={t("aplPanel.code")} active={list.sort === "code"} direction={list.direction} onClick={() => list.changeSort("code")} />
                  <SortableHeader label={t("aplPanel.name")} active={list.sort === "name"} direction={list.direction} onClick={() => list.changeSort("name")} />
                  <SortableHeader label={t("planning.status")} active={list.sort === "status"} direction={list.direction} onClick={() => list.changeSort("status")} />
                  <SortableHeader label={t(k("crm.amount"))} active={list.sort === "estimatedAmount"} direction={list.direction} onClick={() => list.changeSort("estimatedAmount")} />
                  <th>
                    <span className="visually-hidden">
                      {t("administration.actions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        className="record-code-link"
                        onClick={() => setEditing(row)}
                      >
                        {String(row.code ?? row.number ?? "—")}
                      </button>
                    </td>
                    <td>
                      {String(row.name ?? row.subject ?? row.purpose ?? "—")}
                    </td>
                    <td>
                      {t(
                        k(
                          `crm.status.${String(row.status ?? (row.isActive === false ? "Inactive" : "Active"))}`,
                        ),
                      )}
                    </td>
                    <td>
                      {typeof (row.estimatedAmount ?? row.budget) === "number"
                        ? formatNumber(
                            Number(row.estimatedAmount ?? row.budget),
                          )
                        : "—"}
                    </td>
                    <td>
                      {writable && (
                        <Actions
                          section={section}
                          row={row}
                          data={data}
                          reload={load}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!list.rows.length && (
            <p className="empty-state">{t(k("crm.noResults"))}</p>
          )}
        </section>
      )}
    </main>
  );
}

function Overview({ data }: { data: CrmSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <>
      <section className="maintenance-kpis quality-kpis">
        {Object.entries(data.overview).map(([name, value]) => (
          <article
            className="indicator-card neutral maintenance-kpi"
            key={name}
          >
            <span className="indicator-label">{t(k(`crm.kpi.${name}`))}</span>
            <div className="indicator-value">{formatNumber(value)}</div>
          </article>
        ))}
      </section>
      <section className="capacity-overview-grid">
        {(
          [
            "leads",
            "contacts",
            "opportunities",
            "activities",
            "campaigns",
            "consents",
            "pipeline",
            "reports",
          ] as CrmSection[]
        ).map((section) => (
          <Link
            className="capacity-overview-card"
            to={`/app/crm/${route(section)}`}
            key={section}
          >
            <h2>{t(k(`crm.${section}`))}</h2>
            <p>{t(k(`crm.${section}Description`))}</p>
            <span className="capacity-overview-link">
              {t("capacity.openSection")} →
            </span>
          </Link>
        ))}
      </section>
    </>
  );
}
function Reports({ data }: { data: CrmSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <div className="quality-section-stack">
      <Report
        title={t(k("crm.funnel"))}
        rows={data.funnel}
        columns={[
          ["stage", "crm.stage"],
          ["opportunities", "crm.opportunities"],
          ["amount", "crm.amount"],
          ["weightedAmount", "crm.weightedAmount"],
        ]}
        format={formatNumber}
      />
      <Report
        title={t(k("crm.forecast"))}
        rows={data.forecast}
        columns={[
          ["month", "crm.month"],
          ["pipeline", "crm.pipelineAmount"],
          ["weighted", "crm.weightedAmount"],
        ]}
        format={formatNumber}
      />
    </div>
  );
}
function Report({
  title,
  rows,
  columns,
  format,
}: {
  title: string;
  rows: CrmRecord[];
  columns: [string, string][];
  format: (value: number) => string;
}) {
  const { t } = useLocale();
  return (
    <section className="data-panel administration-panel">
      <div className="section-heading">
        <h2>{title}</h2>
      </div>
      <div className="administration-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map(([, label]) => (
                <th key={label}>{t(k(label))}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id ?? index}>
                {columns.map(([field]) => (
                  <td key={field}>
                    {typeof row[field] === "number"
                      ? format(Number(row[field]))
                      : String(row[field] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function Actions({
  section,
  row,
  data,
  reload,
}: {
  section: CrmSection;
  row: CrmRecord;
  data: CrmSnapshot;
  reload: () => Promise<void>;
}) {
  const { t } = useLocale();
  if (section === "leads" && row.status !== "Converted")
    return (
      <>
        <button
          className="secondary-button"
          onClick={async () => {
            await changeCrmStatus("leads", row.id, "Qualified");
            await reload();
          }}
        >
          {t(k("crm.qualify"))}
        </button>
        <button
          className="secondary-button"
          onClick={async () => {
            await convertCrmLead(row.id);
            await reload();
          }}
        >
          {t(k("crm.convert"))}
        </button>
      </>
    );
  if (section === "opportunities" && row.status === "Open")
    return (
      <>
        <select
          aria-label={t(k("crm.stage"))}
          defaultValue=""
          onChange={async (event) => {
            if (event.target.value) {
              await changeCrmStatus("opportunities", row.id, "Stage", {
                pipelineStageId: event.target.value,
              });
              await reload();
            }
          }}
        >
          <option value="">{t(k("crm.changeStage"))}</option>
          {data.stages
            .filter((x) => x.isActive !== false)
            .map((stage) => (
              <option key={stage.id} value={stage.id}>
                {String(stage.name)}
              </option>
            ))}
        </select>
        {row.customerId && !row.salesQuotationId && (
          <button
            className="secondary-button"
            onClick={async () => {
              await createCrmQuotation(row.id);
              await reload();
            }}
          >
            {t(k("crm.createQuotation"))}
          </button>
        )}
      </>
    );
  if (section === "activities" && row.status === "Pending")
    return (
      <button
        className="secondary-button"
        onClick={async () => {
          await changeCrmStatus("activities", row.id, "Completed");
          await reload();
        }}
      >
        {t(k("crm.complete"))}
      </button>
    );
  return null;
}

function CrmEditor({
  section,
  item,
  data,
  cancel,
  saved,
  onPrevious,
  onNext,
}: {
  section: CrmSection;
  item: CrmRecord | null;
  data: CrmSnapshot;
  cancel: () => void;
  saved: () => Promise<void>;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
}) {
  const { t } = useLocale(),
    [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget,
      body: Record<string, unknown> = Object.fromEntries(new FormData(form));
    for (const field of [
      "position",
      "probability",
      "estimatedAmount",
      "budget",
      "actualCost",
    ])
      if (body[field] !== undefined && body[field] !== "")
        body[field] = Number(body[field]);
    for (const field of ["isWon", "isLost", "isActive"])
      body[field] =
        (form.elements.namedItem(field) as HTMLInputElement | null)?.checked ??
        false;
    try {
      await saveCrm(entities[section], body, item?.id);
      await saved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "crm.saveError");
    }
  };
  return (
    <RecordLayout title={t(k(`crm.${section}`))} cancel={cancel} onPrevious={onPrevious} onNext={onNext}>
      {error && (
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      )}
        <form id="crm-form" className="administration-form record-form" onSubmit={submit}>
          <div className="compact-form-grid">
          <Fields section={section} item={item} data={data} />
          </div>
        </form>
    </RecordLayout>
  );
}
function Fields({
  section,
  item,
  data,
}: {
  section: CrmSection;
  item: CrmRecord | null;
  data: CrmSnapshot;
}) {
  const { t } = useLocale();
  if (section === "pipeline")
    return (
      <>
        <Field
          label={t("aplPanel.code")}
          name="code"
          value={item?.code}
          required
        />
        <Field
          label={t("aplPanel.name")}
          name="name"
          value={item?.name}
          required
        />
        <Field
          label={t(k("crm.position"))}
          name="position"
          value={item?.position ?? 1}
          type="number"
          required
        />
        <Field
          label={t(k("crm.probability"))}
          name="probability"
          value={item?.probability ?? 0}
          type="number"
          required
        />
        <Check
          name="isWon"
          label={t(k("crm.won"))}
          checked={item?.isWon === true}
        />
        <Check
          name="isLost"
          label={t(k("crm.lost"))}
          checked={item?.isLost === true}
        />
        <Check
          name="isActive"
          label={t("administration.active")}
          checked={item?.isActive !== false}
        />
      </>
    );
  if (section === "leads")
    return (
      <>
        <Field
          label={t("aplPanel.code")}
          name="code"
          value={item?.code}
          required
        />
        <Field
          label={t("aplPanel.name")}
          name="name"
          value={item?.name}
          required
        />
        <Field
          label={t(k("crm.company"))}
          name="companyName"
          value={item?.companyName}
        />
        <Field
          label={t(k("crm.taxIdentifier"))}
          name="taxIdentifier"
          value={item?.taxIdentifier}
        />
        <Field
          label={t(k("crm.email"))}
          name="email"
          value={item?.email}
          type="email"
        />
        <Field label={t(k("crm.phone"))} name="phone" value={item?.phone} />
        <Field label={t(k("crm.source"))} name="source" value={item?.source} />
        <Area
          label={t("administration.description")}
          name="notes"
          value={item?.notes}
        />
      </>
    );
  if (section === "contacts")
    return (
      <>
        <Field
          label={t("aplPanel.code")}
          name="code"
          value={item?.code}
          required
        />
        <Field
          label={t("aplPanel.name")}
          name="name"
          value={item?.name}
          required
        />
        <Select
          label={t(k("crm.customer"))}
          name="customerId"
          value={item?.customerId}
          rows={data.customers}
        />
        <Select
          label={t(k("crm.lead"))}
          name="leadId"
          value={item?.leadId}
          rows={data.leads}
        />
        <Field
          label={t(k("crm.positionName"))}
          name="positionName"
          value={item?.position}
        />
        <Field
          label={t(k("crm.email"))}
          name="email"
          value={item?.email}
          type="email"
        />
        <Field label={t(k("crm.phone"))} name="phone" value={item?.phone} />
        <Check
          name="isActive"
          label={t("administration.active")}
          checked={item?.isActive !== false}
        />
      </>
    );
  if (section === "opportunities")
    return (
      <>
        <Field
          label={t("quality.number")}
          name="number"
          value={item?.number}
          required
        />
        <Field
          label={t("aplPanel.name")}
          name="name"
          value={item?.name}
          required
        />
        <Select
          label={t(k("crm.stage"))}
          name="pipelineStageId"
          value={item?.pipelineStageId}
          rows={data.stages}
          required
        />
        <Select
          label={t(k("crm.customer"))}
          name="customerId"
          value={item?.customerId}
          rows={data.customers}
        />
        <Select
          label={t(k("crm.lead"))}
          name="leadId"
          value={item?.leadId}
          rows={data.leads}
        />
        <Field
          label={t(k("crm.amount"))}
          name="estimatedAmount"
          value={item?.estimatedAmount ?? 0}
          type="number"
        />
        <Field
          label={t(k("crm.probability"))}
          name="probability"
          value={item?.probability ?? 0}
          type="number"
        />
        <Field
          label={t(k("crm.currency"))}
          name="currencyCode"
          value={item?.currencyCode ?? "EUR"}
          required
        />
        <Field
          label={t(k("crm.expectedCloseDate"))}
          name="expectedCloseDate"
          value={item?.expectedCloseDate}
          type="date"
          required
        />
        <Area
          label={t("administration.description")}
          name="notes"
          value={item?.notes}
        />
      </>
    );
  if (section === "activities")
    return (
      <>
        <Field
          label={t(k("crm.subject"))}
          name="name"
          value={item?.subject}
          required
        />
        <EnumSelect
          label={t(k("crm.activityType"))}
          name="activityType"
          value={item?.type ?? "Task"}
          values={["Task", "Call", "Meeting", "Email", "FollowUp"]}
        />
        <Field
          label={t(k("crm.dueAt"))}
          name="dueAtUtc"
          value={item?.dueAtUtc ? String(item.dueAtUtc).slice(0, 16) : ""}
          type="datetime-local"
          required
        />
        <Select
          label={t(k("crm.opportunity"))}
          name="opportunityId"
          value={item?.opportunityId}
          rows={data.opportunities}
        />
        <Select
          label={t(k("crm.contact"))}
          name="contactId"
          value={item?.contactId}
          rows={data.contacts}
        />
        <Area
          label={t("administration.description")}
          name="description"
          value={item?.description}
        />
      </>
    );
  if (section === "campaigns")
    return (
      <>
        <Field
          label={t("aplPanel.code")}
          name="code"
          value={item?.code}
          required
        />
        <Field
          label={t("aplPanel.name")}
          name="name"
          value={item?.name}
          required
        />
        <EnumSelect
          label={t("planning.status")}
          name="campaignStatus"
          value={item?.status ?? "Draft"}
          values={["Draft", "Planned", "Active", "Completed", "Cancelled"]}
        />
        <Field
          label={t(k("crm.startsOn"))}
          name="startsOn"
          value={item?.startsOn}
          type="date"
        />
        <Field
          label={t(k("crm.endsOn"))}
          name="endsOn"
          value={item?.endsOn}
          type="date"
        />
        <Field
          label={t(k("crm.budget"))}
          name="budget"
          value={item?.budget ?? 0}
          type="number"
        />
        <Field
          label={t(k("crm.actualCost"))}
          name="actualCost"
          value={item?.actualCost ?? 0}
          type="number"
        />
        <Field
          label={t(k("crm.channel"))}
          name="channel"
          value={item?.channel}
        />
        <Area
          label={t("administration.description")}
          name="description"
          value={item?.description}
        />
      </>
    );
  if (section === "campaignMembers")
    return (
      <>
        <Select
          label={t(k("crm.campaign"))}
          name="campaignId"
          value={item?.campaignId}
          rows={data.campaigns}
          required
        />
        <EnumSelect
          label={t(k("crm.memberType"))}
          name="memberType"
          value={item?.memberType ?? "Lead"}
          values={["Lead", "Contact", "Customer"]}
        />
        <Field
          label={t(k("crm.memberId"))}
          name="memberId"
          value={item?.memberId}
          required
        />
        <Field
          label={t(k("crm.response"))}
          name="response"
          value={item?.response}
        />
      </>
    );
  return (
    <>
      <EnumSelect
        label={t(k("crm.subjectType"))}
        name="subjectType"
        value={item?.subjectType ?? "Contact"}
        values={["Lead", "Contact", "Customer"]}
      />
      <Field
        label={t(k("crm.subjectId"))}
        name="subjectId"
        value={item?.subjectId}
        required
      />
      <Field
        label={t(k("crm.purpose"))}
        name="purpose"
        value={item?.purpose}
        required
      />
      <EnumSelect
        label={t("planning.status")}
        name="consentStatus"
        value={item?.status ?? "Unknown"}
        values={["Unknown", "Granted", "Withdrawn"]}
      />
      <Field
        label={t(k("crm.lawfulBasis"))}
        name="lawfulBasis"
        value={item?.lawfulBasis}
      />
      <Area
        label={t(k("crm.evidence"))}
        name="evidence"
        value={item?.evidence}
      />
    </>
  );
}
function RecordLayout({
  title,
  cancel,
  onPrevious,
  onNext,
  children,
}: {
  title: string;
  cancel: () => void;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
  children: ReactNode;
}) {
  const { t } = useLocale();
  return (
    <main className="page-content"><AppModal title={title} closeLabel={t("recordNavigation.backToList")} onClose={cancel} onPrevious={onPrevious} onNext={onNext} saveForm wide>{children}</AppModal></main>
  );
}
function Field({
  label,
  name,
  value,
  type = "text",
  required = false,
}: {
  label: string;
  name: string;
  value?: unknown;
  type?: string;
  required?: boolean;
}) {
  return (
    <label>
      <span>{label}</span>
      <input
        name={name}
        type={type}
        defaultValue={value == null ? "" : String(value)}
        required={required}
        step={type === "number" ? "any" : undefined}
      />
    </label>
  );
}
function Area({
  label,
  name,
  value,
}: {
  label: string;
  name: string;
  value?: unknown;
}) {
  return (
    <label className="form-field-wide">
      <span>{label}</span>
      <textarea name={name} defaultValue={value == null ? "" : String(value)} />
    </label>
  );
}
function Check({
  label,
  name,
  checked,
}: {
  label: string;
  name: string;
  checked: boolean;
}) {
  return (
    <label className="checkbox-label">
      <input type="checkbox" name={name} defaultChecked={checked} />
      <span>{label}</span>
    </label>
  );
}
function Select({
  label,
  name,
  value,
  rows,
  required = false,
}: {
  label: string;
  name: string;
  value?: unknown;
  rows: CrmRecord[];
  required?: boolean;
}) {
  return (
    <label>
      <span>{label}</span>
      <select
        name={name}
        defaultValue={String(value ?? "")}
        required={required}
      >
        <option value="">—</option>
        {rows.map((row) => (
          <option key={row.id} value={row.id}>
            {String(row.code ?? row.number ?? "")} ·{" "}
            {String(row.name ?? row.subject ?? "")}
          </option>
        ))}
      </select>
    </label>
  );
}
function EnumSelect({
  label,
  name,
  value,
  values,
}: {
  label: string;
  name: string;
  value: unknown;
  values: string[];
}) {
  const { t } = useLocale();
  return (
    <label>
      <span>{label}</span>
      <select name={name} defaultValue={String(value)}>
        {values.map((entry) => (
          <option key={entry} value={entry}>
            {t(k(`crm.enum.${entry}`))}
          </option>
        ))}
      </select>
    </label>
  );
}
function route(section: CrmSection) {
  return (
    {
      leads: "leads",
      contacts: "contactos",
      opportunities: "oportunidades",
      activities: "actividades",
      campaigns: "campanas",
      campaignMembers: "audiencias",
      consents: "consentimientos",
      pipeline: "embudo",
      reports: "informes",
    } as Record<string, string>
  )[section];
}
